// Fast smooth-scroll to an element inside its scrollable ancestor.
// Native scrollIntoView/scrollTo({behavior:'smooth'}) have a fixed, slowish
// duration we can't control, so we animate scrollTop ourselves over a short
// duration with easing.
//
// Options:
//   holdUntilStable — instead of a fixed settle tail, keep pinning the target to
//     `offset` until the layout above it stops moving (or `maxHold` ms elapse).
//     Needed when scrolling a *freshly mounted* list (e.g. navigating in from
//     Tags Summary / Audio Stats): the whole list remounts, so rows above the
//     target keep growing for far longer than the 400ms tail as their content
//     lays out — the tail runs out mid-shift and the target ends up parked in
//     the wrong place. A map-marker click doesn't need this: its list is already
//     mounted and settled, so 400ms is plenty.
export const fastScrollToElement = (
  el,
  {
    duration,
    offset = 20,
    container,
    settleTail = 400,
    holdUntilStable = false,
    maxHold = 2500,
  } = {}
) => {
  if (!el) return;
  const scroller =
    container ||
    el.closest('.locations-list') ||
    el.closest('.locations-list-container');
  if (!scroller) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return;
  }
  // How far we still need to scroll to put the element's top at `offset`,
  // clamped to the real scroll range. Re-measured every frame so layout that
  // shifts above the target during the scroll (lazy images, async fields)
  // doesn't leave the element overscrolled past the top (clipped header).
  const remaining = () => {
    const max = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
    const want =
      scroller.scrollTop +
      (el.getBoundingClientRect().top - scroller.getBoundingClientRect().top) -
      offset;
    return Math.min(max, Math.max(0, want)) - scroller.scrollTop;
  };
  if (Math.abs(remaining()) < 2 && !holdUntilStable) return;
  // Fixed, short duration so far targets don't get a long coast (the
  // "inertia"). Distance-scaling made far scrolls longer, which read worse.
  const dur = duration ?? 250;
  // While we're driving scrollTop ourselves, the browser's scroll-anchoring can
  // also nudge it to "hold" content as rows above the target grow — that fights
  // our animation and trips the user-grabbed-it guard below. On a settled list
  // this never fires (no growth), but a fresh mount grows a lot, so disable it
  // for the duration when holding, then restore.
  const prevAnchor = holdUntilStable ? scroller.style.overflowAnchor : null;
  if (holdUntilStable) scroller.style.overflowAnchor = 'none';
  const restore = () => {
    if (holdUntilStable) scroller.style.overflowAnchor = prevAnchor || '';
  };
  const startTime = performance.now();
  const easeOutQuad = (t) => 1 - (1 - t) * (1 - t);
  let easedPrev = 0;
  let lastSet = null;
  // Frames in a row the target needed no correction — the layout went quiet.
  let quietFrames = 0;
  const step = (now) => {
    // If something other than us moved the scroller (the user grabbed it),
    // back off immediately instead of fighting them.
    if (lastSet !== null && Math.abs(scroller.scrollTop - lastSet) > 2) {
      restore();
      return;
    }
    const elapsed = now - startTime;
    const t = Math.min(1, elapsed / dur);
    const eased = easeOutQuad(t);
    // `rem` measured BEFORE we re-pin = how far the target drifted since last
    // frame (layout above it moving). During the ease we consume the eased
    // fraction of it; once eased hits 1 we consume all of it, holding pinned.
    const rem = remaining();
    const frac = eased >= 1 ? 1 : (eased - easedPrev) / (1 - easedPrev);
    scroller.scrollTop += rem * frac;
    lastSet = scroller.scrollTop;
    easedPrev = eased;

    if (t < 1) {
      requestAnimationFrame(step);
      return;
    }
    // Ease done — we're now holding the target on `offset`.
    if (holdUntilStable) {
      // `rem` here is the drift accumulated over the previous frame. Once it's
      // ~0 for a few frames in a row, the list has finished laying out and we
      // can stop. `maxHold` caps a list that never settles.
      if (Math.abs(rem) < 1) {
        if (++quietFrames >= 4) {
          restore();
          return;
        }
      } else {
        quietFrames = 0;
      }
      if (elapsed < dur + maxHold) requestAnimationFrame(step);
      else restore();
    } else if (elapsed < dur + settleTail) {
      requestAnimationFrame(step);
    }
  };
  requestAnimationFrame(step);
};
